import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ActivityResponse,
  ActivityWeeklySummary,
  LogActivityInput,
  PulseReplyContextResponse,
  SendPulseComposeInput,
  SendPulseReplyResponse,
  StaleLeadsResponse,
} from '@orion/contracts';
import { api, getPage } from '../../lib/api-client.js';

interface ActivityFeedFilter {
  page: number;
  pageSize: number;
  kind?: string;
  occurredFrom?: string;
  occurredTo?: string;
  actorId?: string;
}

export function useActivityFeedQuery(filter: ActivityFeedFilter) {
  return useQuery({
    queryKey: ['activity-feed', filter],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(filter.page));
      params.set('pageSize', String(filter.pageSize));
      if (filter.kind) params.set('kind', filter.kind);
      if (filter.occurredFrom) params.set('occurredFrom', filter.occurredFrom);
      if (filter.occurredTo) params.set('occurredTo', filter.occurredTo);
      if (filter.actorId) params.set('actorId', filter.actorId);
      return getPage<ActivityResponse[]>(`/activities/feed?${params.toString()}`);
    },
    placeholderData: (prev) => prev,
  });
}

export function useActivityWeeklySummaryQuery(from: string, to: string) {
  return useQuery({
    queryKey: ['activity-feed', 'summary', from, to],
    queryFn: () => api.get<ActivityWeeklySummary>(`/activities/feed/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  });
}

export function useStaleLeadsQuery() {
  return useQuery({ queryKey: ['activity-feed', 'stale-leads'], queryFn: () => api.get<StaleLeadsResponse>('/activities/feed/stale-leads') });
}

interface ActivitiesFilter {
  leadId?: string;
  contactId?: string;
  companyId?: string;
  opportunityId?: string;
}

export function useActivitiesQuery(filter: ActivitiesFilter) {
  const key = Object.entries(filter).find(([, v]) => v);
  return useQuery({
    queryKey: ['activities', filter],
    queryFn: () => {
      const params = new URLSearchParams(filter as Record<string, string>);
      return getPage<ActivityResponse[]>(`/activities?${params.toString()}`);
    },
    enabled: Boolean(key),
  });
}

/** The record-detail "log activity" composer (Note/Call/Email/Meeting). */
export function useLogActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LogActivityInput) => api.post<ActivityResponse>('/activities/log', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['activities'] }),
  });
}

/** Whether a lead's Email tab can actually send (vs. only log) — true only
 * for leads with an Inbox Pulse thread on record. */
export function usePulseReplyContextQuery(leadId: string | undefined) {
  return useQuery({
    queryKey: ['pulse-reply-context', leadId],
    queryFn: () => api.get<PulseReplyContextResponse>(`/pulse/leads/${leadId}/reply-context`),
    enabled: Boolean(leadId),
  });
}

/** Sends a real, threaded reply through the lead's connected Inbox Pulse
 * mailbox — unlike `useLogActivity`, the lead actually receives this.
 * `connectionId` picks which mailbox when `PulseReplyContextResponse`
 * reported more than one option; omit it to use the default (most recent). */
export function useSendPulseReply(leadId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bodyText, connectionId }: { bodyText: string; connectionId?: string }) =>
      api.post<SendPulseReplyResponse>(`/pulse/leads/${leadId}/reply`, { bodyText, connectionId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['activities'] }),
  });
}

/** Starts a brand new Inbox Pulse thread with a lead that has none yet
 * (`PulseReplyContextResponse.canCompose`) — e.g. one created directly via
 * the MCP tools, a web form, or any other non-email source. Unlike
 * `useSendPulseReply`, the mailbox is required: there's no "most recent
 * thread" to default to. */
export function useSendPulseCompose(leadId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SendPulseComposeInput) => api.post<SendPulseReplyResponse>(`/pulse/leads/${leadId}/compose`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['pulse-reply-context', leadId] });
    },
  });
}
